// Maeck Office — the 3D building (SPEC §10). Public entry point: createScene(container, options).
//
// A floor per project, stacked in a building. Floor view: one floor at the origin, seen from an
// isometric orthographic camera; the others wait far above/below. Switching floors slides them past
// a fixed elevator cabin (the "ride"). Building view: every floor stacked with gaps, flatter camera.
// Rendering is on demand and paced (pacing.js): 60 fps for camera motion, 24 fps while agents work,
// 8 fps for idle breathing, no redraw at all when nothing moves or while the page is hidden. A floor
// may have pets, a chicken (« La poule de l’étage », chicken.js) and a cat (« Le chat de l’étage »,
// cat.js): 24 fps while one moves, nothing while they rest (a timer wakes the loop when a rest ends).
import {
  Box3, CylinderGeometry, DirectionalLight, DoubleSide, Group, HemisphereLight, Mesh, MeshBasicMaterial,
  MeshStandardMaterial, NeutralToneMapping, PCFShadowMap, PlaneGeometry, Ray, Scene, Sprite, SRGBColorSpace,
  Vector3, WebGLRenderer, CanvasTexture,
} from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import {
  DIMS, floorOffsets, organizeOffice, officeSignature, hashString, floorLayout, buildingSlab,
} from './layout.js';
import {
  CARD, TAG_PAD, fitItems, layoutCards, convexHull, pillWidth,
} from './framing.js';
import { themeTokens, lighten, mixHex } from './palette.js';
import { Tweens, ease, lerp, clamp } from './anim.js';
import { createGeometries, disposeAll } from './geometries.js';
import { createMaterials, applyTheme, disposeMaterials } from './materials.js';
import { drawCabinDisplay, FONT_MONO } from './textures.js';
import { FloorView } from './floor.js';
import { Particles, Pulses } from './effects.js';
import { CameraRig, ISO_ELEV, BUILDING_ELEV } from './camera.js';
import { attachControls } from './input.js';
import { normalizeStatus } from './agents.js';
import {
  SCENE_PACING, targetFps, throttleFrame, scenePixelRatio, wantsAntialias,
} from './pacing.js';
import { chickenFloorIds, chickenFps } from './chicken.js';
import { catFloorIds, catFps } from './cat.js';

const API_METHODS = ['setOffice', 'setAgentStatus', 'setDeptStats', 'showFloor', 'showBuilding', 'focusDept',
  'resetView', 'zoom', 'pulseConnector', 'getAnchors', 'setTheme', 'resize', 'dispose',
  'highlightDept', 'setViewInsets', 'setCardMetrics', 'setReducedMotion', 'getStats', 'debug'];

// The floors' pets (decoration): the floors that have one (from the office) and the frame rate it
// asks for (floor.js setPet draws it).
const PETS = Object.freeze([
  Object.freeze({ kind: 'chicken', floorIds: chickenFloorIds, fps: chickenFps }),
  Object.freeze({ kind: 'cat', floorIds: catFloorIds, fps: catFps }),
]);

const SUN_DIR = new Vector3(0.34, 0.64, -1).normalize();
const FILL_DIR = new Vector3(1, 0.6, 0.9).normalize();
// The elevator ride (SPEC §10: ≈ 900 ms); cards and name tags come back for its last stretch.
const FLOOR_SWITCH_MS = 860;
const VIEW_SWITCH_MS = 1000;
const REVEAL_AT = 0.88;
const CAMERA_MS = 650;
// Floor view: the glass shaft fades out over its first SHAFT_FADE_UNITS (floor.js); building view: whole.
const SHAFT_FADE = 1;
// Height above a pod's top where an agent's name tag hangs (DOM tag above the seated head).
const AGENT_TAG_Y = 1.66;
// Floor view framing: the smaller side of the free area shows at least MIN_VIEW_UNITS world units
// and no floor gets more than MAX_SCALE_RATIO times the scale of the fullest one (a floor with one
// department is not blown up); the margin kept around the floor (px).
const MIN_VIEW_UNITS = 12;
const MAX_SCALE_RATIO = 1.35;
// Full department cards are used when they cost the pods less than this share of their size, or
// when the pods stay at least COMFORT_SCALE px per world unit with them.
const CARDS_MIN_SHARE = 0.84;
const COMFORT_SCALE = 30;
const FIT_MARGIN = 18;
const FIT_MARGIN_SMALL = 10;
// Building view: the floor labels hang this far (px) right of each slab's right corner.
const FLOOR_LABEL_GAP = 18;
const ORIGIN = Object.freeze({ x: 0, y: 0, z: 0 });

/**
 * A scene that does nothing (no DOM, no WebGL): same API, used when WebGL is unavailable.
 * @returns {object}
 */
export function createNoopScene() {
  const api = {};
  for (const m of API_METHODS) api[m] = () => {};
  api.getAnchors = () => [];
  api.getStats = () => ({ fps: 0, drawCalls: 0, triangles: 0, floors: 0, agents: 0 });
  return api;
}

function webglAvailable() {
  try {
    const c = document.createElement('canvas');
    const gl = c.getContext('webgl2') || c.getContext('webgl');
    // Release the probe context right away (browsers cap the number of live contexts).
    gl?.getExtension('WEBGL_lose_context')?.loseContext();
    return !!gl;
  } catch {
    return false;
  }
}

/** Reads `{animate}` from an options argument that may be missing or null (default true). */
function wantsAnimation(opts) {
  return !(opts && typeof opts === 'object' && opts.animate === false);
}

function showFallback(container) {
  const note = document.createElement('div');
  note.textContent = 'La vue 3D n’est pas disponible sur cet appareil.';
  note.style.position = 'absolute';
  note.style.inset = '0';
  note.style.display = 'flex';
  note.style.alignItems = 'center';
  note.style.justifyContent = 'center';
  note.style.font = '500 12px "IBM Plex Mono", monospace';
  note.style.letterSpacing = '0.08em';
  note.style.textTransform = 'uppercase';
  note.style.opacity = '0.6';
  container.appendChild(note);
  return note;
}

/**
 * Creates the 3D office scene inside `container` (which should be positioned; the canvas fills it).
 * `reducedMotion` is the initial value (default: the system preference); later changes of the
 * system preference are always followed, and setReducedMotion() overrides at any time.
 * @param {HTMLElement} container
 * @param {{theme?:'light'|'dark', reducedMotion?:boolean, onAgentClick?:(id:string)=>void,
 *   onDeptClick?:(id:string)=>void, onFloorClick?:(id:string)=>void,
 *   onHover?:(hit:{kind:'agent'|'dept'|'floor', id:string}|null)=>void,
 *   onAnchors?:(anchors:Array<object>)=>void, insets?:{top?:number,right?:number,bottom?:number,left?:number}}} [options]
 * @returns {object} the scene API (SPEC §10)
 */
export function createScene(container, options = {}) {
  if (typeof window === 'undefined' || typeof document === 'undefined' || !container || !container.appendChild) {
    return createNoopScene();
  }
  if (!webglAvailable()) {
    const note = showFallback(container);
    const api = createNoopScene();
    api.dispose = () => note.remove();
    return api;
  }
  let renderer;
  try {
    // Low-power GPU (a dual-GPU Mac keeps its integrated chip); MSAA only below Retina density.
    renderer = new WebGLRenderer({
      antialias: wantsAntialias(window.devicePixelRatio), alpha: true, powerPreference: 'low-power',
    });
  } catch {
    const note = showFallback(container);
    const api = createNoopScene();
    api.dispose = () => note.remove();
    return api;
  }

  const cb = {
    agent: options.onAgentClick, dept: options.onDeptClick, floor: options.onFloorClick,
    hover: options.onHover, anchors: options.onAnchors,
  };
  const coarse = !!window.matchMedia?.('(pointer: coarse)').matches;
  const mqReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  let reducedMotion = typeof options.reducedMotion === 'boolean' ? options.reducedMotion : !!mqReduced?.matches;
  // Dev tooling only: keep rendering when the page reports itself hidden (embedded previews).
  const isHidden = () => options.ignoreHidden !== true && document.hidden;

  // Renderer.
  renderer.setPixelRatio(scenePixelRatio(window.devicePixelRatio));
  renderer.outputColorSpace = SRGBColorSpace;
  renderer.toneMapping = NeutralToneMapping;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = PCFShadowMap;
  renderer.setClearColor(0x000000, 0);
  const canvas = renderer.domElement;
  canvas.style.display = 'block';
  canvas.style.width = '100%';
  canvas.style.height = '100%';
  canvas.style.touchAction = 'none';
  canvas.style.outline = 'none';
  canvas.setAttribute('aria-label', 'Vue 3D du bureau');
  canvas.setAttribute('role', 'img');
  container.appendChild(canvas);

  let tokens = themeTokens(options.theme);
  const geos = createGeometries();
  const mats = createMaterials(tokens);
  const scene = new Scene();
  const root = new Group();
  scene.add(root);

  // Lights.
  const hemi = new HemisphereLight();
  const sun = new DirectionalLight();
  sun.castShadow = true;
  sun.shadow.mapSize.set(SCENE_PACING.shadowMapSize, SCENE_PACING.shadowMapSize);
  sun.shadow.bias = -0.0004;
  sun.shadow.normalBias = 0.025;
  sun.shadow.radius = 4;
  const fill = new DirectionalLight();
  scene.add(hemi, sun, sun.target, fill, fill.target);

  // Ground: an invisible plane that only shows shadows (the page background shows through).
  const groundGeo = new PlaneGeometry(600, 600).rotateX(-Math.PI / 2);
  const ground = new Mesh(groundGeo, mats.ground);
  ground.receiveShadow = true;
  ground.renderOrder = -1;
  scene.add(ground);

  // The elevator cabin (one for the whole building; floors slide past it) and the glass shaft
  // that surrounds it while riding between floors.
  const cabin = buildCabin();
  scene.add(cabin.group);
  const ride = buildRideShaft();
  scene.add(ride.group);

  // Effects.
  const particles = new Particles(mats);
  scene.add(particles.points);
  const pulses = new Pulses(scene, geos, mats);

  // Hover ring under an agent.
  const hoverRingMat = new MeshBasicMaterial({ transparent: true, depthWrite: false, opacity: 0.85, toneMapped: false });
  const hoverRing = new Mesh(geos.ring, hoverRingMat);
  hoverRing.visible = false;
  hoverRing.renderOrder = 2;
  scene.add(hoverRing);

  // Invisible pet parts kept in the scene: their programs (the only vertex-coloured materials) are
  // compiled with the others (setOffice), so the first chicken or cat shown does not stall a frame.
  for (const [geo, mat] of [[geos.chickenBody, mats.chicken], [geos.catBody, mats.cat]]) {
    const warm = new Mesh(geo, mat);
    warm.visible = false;
    warm.raycast = () => {};
    scene.add(warm);
  }

  const rig = new CameraRig();
  if (options.insets) rig.setInsets(options.insets);
  const tweens = new Tweens();

  /** @type {FloorView[]} */
  let floors = [];
  let signature = null;
  const agentFloor = new Map();
  const deptFloor = new Map();
  const statuses = new Map();
  const deptStats = new Map();
  const view = { mode: 'floor', current: 0, transitioning: false, revealed: false };
  // Size of the HTML department cards (the UI measures them: setCardMetrics); compact = pills only;
  // labelW = widest floor label of the building view (0 until measured).
  let cardMetrics = { w: CARD.w, h: CARD.h, gap: CARD.gap, compact: false, labelW: 0 };
  // Card layout of the current floor for the anchors, recomputed only when the camera moves.
  let placementKey = '';
  let placement = new Map();
  const vis = { buildingT: 0, shaftFade: SHAFT_FADE };
  let userAdjusted = false;
  let externalDept = null;
  let hover = null;
  let hoverPending = null;
  let interacting = false;
  const kinetic = new Vector3();
  let panSamples = [];
  let time = 0;
  // Scene time until which frames keep coming after a motion-mode change (poses settle).
  let settleUntil = 0;
  let disposed = false;
  let raf = 0;
  // Pending timeout before the next paced frame (slow rates do not wake up at every vsync).
  let timer = 0;
  // A state change asked for a frame: draw it at the next vsync, whatever the pace.
  let dirty = true;
  let last = 0;
  let lastRender = 0;
  let agentsActive = false;
  // Pet kind → the floors that have one (floor.chicken / floor.cat === true); the pets of the current
  // floor ask for frames while they move. Their rests follow the wall clock (s, rAF time base).
  const petIds = new Map(PETS.map((p) => [p.kind, new Set()]));
  // Pet kind → floor id → where it stood, kept across rebuilds of the floors (setOffice) until that
  // floor shows it again.
  const petCarry = new Map(PETS.map((p) => [p.kind, new Map()]));
  let petsActive = false;
  let clockS = performance.now() / 1000;
  // avoid: the floor's chicken, which its cat keeps clear of (cat.js).
  const petEnv = { hidden: false, building: false, riding: false, reducedMotion: false, avoid: null };
  let anchors = [];
  let anchorsDirty = true;
  const fps = { frames: 0, since: 0, value: 0 };

  applyLightsTheme();

  // ---------------------------------------------------------------- building helpers

  function buildCabin() {
    const group = new Group();
    const body = new RoundedBoxGeometry(1.06, 1.5, 1.06, 3, 0.08);
    body.translate(0, 0.75, 0);
    const bodyMesh = new Mesh(body, mats.cabin);
    bodyMesh.castShadow = true;
    const doorGeo = new RoundedBoxGeometry(0.4, 1.14, 0.03, 2, 0.01);
    const doors = [-1, 1].map((s) => {
      const d = new Mesh(doorGeo, mats.cabinDoor);
      d.position.set(s * 0.205, 0.64, 0.535);
      return d;
    });
    const displayCanvas = drawCabinDisplay(null, '1');
    const displayTex = new CanvasTexture(displayCanvas);
    displayTex.colorSpace = SRGBColorSpace;
    const displayMat = new MeshBasicMaterial({ map: displayTex, toneMapped: false });
    const displayGeo = new PlaneGeometry(0.34, 0.17);
    const display = new Mesh(displayGeo, displayMat);
    display.position.set(0, 1.33, 0.537);
    const face = new Group();
    face.add(bodyMesh, ...doors, display);
    face.rotation.y = Math.PI / 4;
    const halo = new Sprite(mats.cabinHalo);
    halo.position.set(0, 0.9, 0);
    halo.scale.setScalar(3.2);
    halo.renderOrder = 4;
    group.add(face, halo);
    group.position.y = DIMS.hallH;
    return {
      group, halo, displayCanvas, displayTex,
      dispose() {
        body.dispose(); doorGeo.dispose(); displayGeo.dispose(); displayMat.dispose(); displayTex.dispose();
      },
    };
  }

  // A short section of glass around the cabin (fading out above and below), only while riding.
  function buildRideShaft() {
    const group = new Group();
    const H = 26;
    const glassGeo = new CylinderGeometry(DIMS.shaftR, DIMS.shaftR, H, 56, 1, true);
    glassGeo.translate(0, DIMS.hallH + 0.75, 0);
    const glassMat = new MeshStandardMaterial({
      transparent: true, roughness: 0.1, side: DoubleSide, depthWrite: false, opacity: 0, alphaMap: mats.tx.rideFade,
    });
    const glass = new Mesh(glassGeo, glassMat);
    glass.renderOrder = 3;
    const lines = [];
    for (let k = 0; k < 6; k++) {
      const a = Math.PI / 4 + (k * Math.PI) / 3;
      const c = new CylinderGeometry(0.032, 0.032, H, 6, 1, true);
      c.translate(Math.cos(a) * DIMS.shaftR, DIMS.hallH + 0.75, Math.sin(a) * DIMS.shaftR);
      lines.push(c);
    }
    const cables = new CylinderGeometry(0.012, 0.012, H / 2, 4, 1, true);
    cables.translate(0.18, DIMS.hallH + 1.5 + H / 4, 0);
    const cables2 = cables.clone().translate(-0.36, 0, 0);
    lines.push(cables, cables2);
    const frameGeo = mergeGeometries(lines.map((g) => g.toNonIndexed()), false);
    lines.forEach((g) => g.dispose());
    const frameMat = new MeshStandardMaterial({
      transparent: true, roughness: 0.4, metalness: 0.3, opacity: 0, depthWrite: false, alphaMap: mats.tx.rideFade,
    });
    const frame = new Mesh(frameGeo, frameMat);
    group.add(glass, frame);
    group.visible = false;
    return {
      group,
      set(k) {
        group.visible = k > 0.001;
        glassMat.color.set(tokens.glass);
        glassMat.opacity = tokens.glassOpacity * k;
        frameMat.color.set(tokens.glassFrame);
        frameMat.opacity = k;
      },
      dispose() {
        glassGeo.dispose(); glassMat.dispose(); frameGeo.dispose(); frameMat.dispose();
      },
    };
  }

  function setCabinDisplay(text) {
    drawCabinDisplay(cabin.displayCanvas, text);
    cabin.displayTex.needsUpdate = true;
  }

  /** The number shown for floor `i` (its real order + 1, see layout.floorNumber). */
  function numberOf(i) {
    return String(floors[i]?.number ?? i + 1);
  }

  function applyLightsTheme() {
    hemi.color.set(tokens.hemiSky);
    hemi.groundColor.set(tokens.hemiGround);
    hemi.intensity = tokens.hemiIntensity;
    sun.color.set(tokens.sun);
    sun.intensity = tokens.sunIntensity;
    sun.shadow.intensity = tokens.shadowIntensity;
    fill.color.set(tokens.fill);
    fill.intensity = tokens.fillIntensity;
    renderer.toneMappingExposure = tokens.exposure;
    cabin.halo.visible = tokens.haloStrength > 0;
  }

  /** Fits the sun's shadow frustum around a floor placed at y = 0. */
  function fitShadow(fv) {
    const b = fv ? fv.layout.bounds : { minX: -8, maxX: 8, minZ: -8, maxZ: 8 };
    const cx = (b.minX + b.maxX) / 2;
    const cz = (b.minZ + b.maxZ) / 2;
    const r = Math.hypot(b.maxX - b.minX, b.maxZ - b.minZ) / 2 + 1.5;
    const center = new Vector3(cx, 0, cz);
    sun.target.position.copy(center);
    sun.position.copy(center).addScaledVector(SUN_DIR, 80);
    const cam = sun.shadow.camera;
    cam.left = -r; cam.right = r; cam.top = r; cam.bottom = -r;
    cam.near = 80 - r - 6; cam.far = 80 + r + 6;
    cam.updateProjectionMatrix();
    fill.target.position.copy(center);
    fill.position.copy(center).addScaledVector(FILL_DIR, 60);
  }

  /**
   * Floor view: only the current floor casts sun shadows. Building view: nobody does (a floor
   * would darken the ones below); contact shadows keep everything grounded.
   */
  function updateCasters() {
    floors.forEach((f, i) => f.setCastShadow(view.mode === 'floor' && i === view.current));
  }

  // ---------------------------------------------------------------- view states

  function floorFitBox(fv) {
    const b = fv.layout.bounds;
    return new Box3(new Vector3(b.minX, 0, b.minZ), new Vector3(b.maxX, 2.4, b.maxZ));
  }

  /**
   * Pixels kept free right of the building for the floor labels (they hang 18 px right of each
   * slab's corner), so they never cover the pods of the floors.
   */
  function floorLabelRoom() {
    const w = cardMetrics.labelW || (cardMetrics.compact ? 130 : 210);
    return w + FLOOR_LABEL_GAP + 8;
  }

  function buildingFitBox(current) {
    const box = new Box3();
    const ys = floorOffsets(floors.length, current, 'building');
    floors.forEach((f, i) => {
      const s = f.layout.slab;
      box.expandByPoint(new Vector3(s.x - s.w / 2, ys[i] - DIMS.slabH, s.z - s.d / 2));
      box.expandByPoint(new Vector3(s.x + s.w / 2, ys[i] + (f.isTop ? f.shaftH + DIMS.hallH : 2.4), s.z + s.d / 2));
    });
    return box;
  }

  // ---------------------------------------------------------------- floor framing (framing.js)

  /**
   * World points of a floor used for framing and card placement (cached on the floor view): per pod
   * its base and top corners and the tall things standing on it (people with their name tags, the
   * lamp, the shelf, the plants), each with the pixels it rises above its point on screen.
   */
  function floorGeo(fv) {
    if (fv.frameGeo) return fv.frameGeo;
    const D = DIMS;
    const content = [];
    const heads = new Map();
    for (const r of fv.crowd.rigs) {
      if (!heads.has(r.info.deptId)) heads.set(r.info.deptId, []);
      heads.get(r.info.deptId).push({ x: r.info.seatX, y: D.podH + AGENT_TAG_Y, z: r.info.seatZ, pad: TAG_PAD });
    }
    const pods = fv.pods.map((p) => {
      const base = [];
      const surface = [];
      for (const x of [p.x - p.w / 2, p.x + p.w / 2]) {
        for (const z of [p.z - p.d / 2, p.z + p.d / 2]) {
          base.push({ x, y: 0, z });
          surface.push({ x, y: D.podH, z });
        }
      }
      const hw = p.w / 2;
      const hd = p.d / 2;
      const shelfX = p.x + (p.grid?.block ? p.grid.block.x : 0.6);
      const tall = [
        ...(heads.get(p.deptId) || []),
        { x: p.x - hw + 0.55, y: D.podH + 1.78, z: p.z - hd + 0.55, pad: 3 },              // lamp
        { x: shelfX - 0.82, y: D.podH + 1.34, z: p.z - hd + 0.3, pad: 2 },                  // shelf
        { x: shelfX + 0.82, y: D.podH + 1.34, z: p.z - hd + 0.3, pad: 2 },
        { x: p.x + hw - 0.62, y: D.podH + 1.5, z: p.z - hd + 0.62, pad: 2 },               // plants
        { x: p.x - hw + 0.62, y: D.podH + 1.5, z: p.z + hd - 0.62, pad: 2 },
      ];
      content.push(...base, ...surface, ...tall);
      const name = p.dept?.name || '';
      return { id: p.deptId, name, pillW: pillWidth(name), center: { x: p.x, y: D.podH, z: p.z }, base, surface, tall };
    });
    for (let k = 0; k < 20; k++) {
      const a = (k / 20) * Math.PI * 2;
      const x = Math.cos(a) * D.hallR;
      const z = Math.sin(a) * D.hallR;
      content.push({ x, y: 0, z }, { x, y: D.hallH, z });
    }
    const cabinPts = [];
    for (const x of [-0.62, 0.62]) for (const z of [-0.62, 0.62]) for (const y of [D.hallH, D.hallH + 1.7]) cabinPts.push({ x, y, z });
    content.push(...cabinPts);
    fv.frameGeo = { content, pods, cabin: cabinPts };
    return fv.frameGeo;
  }

  /**
   * Screen shapes of a floor's pods for framing.layoutCards, from a projector world → px: outline
   * (desks, people and their name tags, props), top anchor (above the tallest thing), bottom, left
   * and right.
   */
  function podShapes(geo, proj) {
    return geo.pods.map((pg) => {
      const base = pg.base.map(proj);
      const surface = pg.surface.map(proj);
      const tall = pg.tall.map((p) => {
        const q = proj(p);
        return { x: q.x, y: q.y - (p.pad || 0) };
      });
      const hull = convexHull([...base, ...surface, ...tall]);
      const c = proj(pg.center);
      const side = (sign) => {
        let best = 0;
        for (let i = 1; i < 4; i++) if (sign * (surface[i].x - surface[best].x) > 0) best = i;
        return { x: surface[best].x, y: (surface[best].y + base[best].y) / 2 };
      };
      return {
        id: pg.id, name: pg.name, pillW: pg.pillW, hull, podX: c.x, podY: c.y,
        top: { x: c.x, y: Math.min(...tall.map((p) => p.y), ...surface.map((p) => p.y - 6)) },
        bottom: { x: c.x, y: Math.max(...base.map((p) => p.y)) },
        left: side(-1), right: side(1),
      };
    });
  }

  /** Free part of the canvas (px): what the elevator, the task panel or the sheet leave visible. */
  function freeArea() {
    const { insets, width, height } = rig;
    const { w, h } = rig.area();
    const left = Math.min(insets.left, width - w);
    const top = Math.min(insets.top, height - h);
    return { left, top, right: left + w, bottom: top + h };
  }

  let fitCache = { key: '', value: null };
  let capCache = { key: '', value: 0 };

  /**
   * Largest scale a floor may get: floors keep a similar scale (a floor with one department is at
   * most MAX_SCALE_RATIO times the size the fullest floor gets), and the free area's smaller side
   * always shows at least MIN_VIEW_UNITS world units.
   */
  function scaleCap(area, margin) {
    const key = [signature, area.left, area.top, area.right, area.bottom, margin].join('|');
    if (capCache.key === key) return capCache.value;
    const abs = Math.max(4, Math.min(area.right - area.left, area.bottom - area.top) / MIN_VIEW_UNITS);
    const { right, up } = rig.basis(ISO_ELEV);
    let fullest = Infinity;
    for (const f of floors) {
      if (!f.pods.length) continue;
      const pts = floorGeo(f).content.map((p) => ({
        r: p.x * right.x + p.y * right.y + p.z * right.z, u: p.x * up.x + p.y * up.y + p.z * up.z, top: -(p.pad || 0),
      }));
      fullest = Math.min(fullest, fitItems(pts, area, { margin, minScale: 2, maxScale: 1000 }).scale);
    }
    const value = Math.max(4, Math.min(abs, Number.isFinite(fullest) ? fullest * MAX_SCALE_RATIO : abs));
    capCache = { key, value };
    return value;
  }

  /**
   * Camera framing of a floor: as large as possible in the free area, with room for the department
   * cards laid out around the pods (the layout depends on the scale, so it is refined a few times).
   * @returns {{target: Vector3, viewH: number}}
   */
  function frameFloor(fv) {
    const area = freeArea();
    const small = Math.min(rig.width, rig.height) < 520;
    const key = [fv.id, signature, rig.width, rig.height, area.left, area.top, area.right, area.bottom,
      cardMetrics.w, cardMetrics.h, cardMetrics.compact].join('|');
    if (fitCache.key === key) return { target: fitCache.value.target.clone(), viewH: fitCache.value.viewH };
    const geo = floorGeo(fv);
    const { right, up } = rig.basis(ISO_ELEV);
    const R = (p) => p.x * right.x + p.y * right.y + p.z * right.z;
    const U = (p) => p.x * up.x + p.y * up.y + p.z * up.z;
    const base = geo.content.map((p) => ({ r: R(p), u: U(p), top: -(p.pad || 0) }));
    // Basis coordinates of each pod's card reference points (the same points as podShapes; the top
    // one depends on the scale: the tallest thing on screen once its pixel rise is added).
    const refsAt = (pg, s) => {
      const side = (sign) => {
        let best = 0;
        for (let i = 1; i < 4; i++) if (sign * (R(pg.surface[i]) - R(pg.surface[best])) > 0) best = i;
        return { r: R(pg.surface[best]), u: (U(pg.surface[best]) + U(pg.base[best])) / 2 };
      };
      let topU = -Infinity;
      for (const p of pg.tall) topU = Math.max(topU, U(p) + (p.pad || 0) / s);
      for (const p of pg.surface) topU = Math.max(topU, U(p) + 6 / s);
      return {
        top: { r: R(pg.center), u: topU },
        bottom: { r: R(pg.center), u: Math.min(...pg.base.map(U)) },
        left: side(-1),
        right: side(1),
      };
    };
    const margin = small ? FIT_MARGIN_SMALL : FIT_MARGIN;
    const opts = { margin, minScale: 2, maxScale: scaleCap(area, margin) };
    const content = fitItems(base, area, opts);
    const withCards = (policy) => {
      let fit = content;
      for (let k = 0; k < 3 && geo.pods.length; k++) {
        const s = fit.scale;
        const proj = (p) => ({ x: R(p) * s, y: -U(p) * s });
        const placement = layoutCards(podShapes(geo, proj), {
          ...policy, card: cardMetrics, gap: cardMetrics.gap, center: proj(ORIGIN),
          obstacles: [convexHull(geo.cabin.map(proj))],
        });
        const items = base.slice();
        geo.pods.forEach((pg) => {
          const pl = placement.get(pg.id);
          const ref = pl && refsAt(pg, s)[pl.ref];
          if (!ref) return;
          const px = ref.r * s;
          const py = -ref.u * s;
          items.push({ r: ref.r, u: ref.u, left: pl.rect.left - px, right: pl.rect.right - px, top: pl.rect.top - py, bottom: pl.rect.bottom - py });
        });
        const next = fitItems(items, area, opts);
        const settled = Math.abs(next.scale - s) < 0.01 * s;
        fit = next;
        if (settled) break;
      }
      return fit;
    };
    // Full cards everywhere, else full cards above the pods and pills elsewhere, else pills only:
    // the first that keeps the pods at least CARDS_MIN_SHARE of their size with pills only, or
    // comfortably large anyway (COMFORT_SCALE px per unit).
    const pills = withCards({ compact: true });
    let policy = { compact: true };
    let fit = pills;
    if (!cardMetrics.compact) {
      for (const p of [{ compact: false }, { compact: false, cardsAbove: true }]) {
        const f = withCards(p);
        if (f.scale >= CARDS_MIN_SHARE * pills.scale || f.scale >= COMFORT_SCALE) { policy = p; fit = f; break; }
      }
    }
    const cam = rig.frameFromFit(fit, ISO_ELEV, 0);
    fitCache = { key, value: { target: cam.target.clone(), viewH: cam.viewH, policy } };
    return cam;
  }

  /** Card policy chosen by the framing of the current floor (full cards, cards above, pills). */
  function cardPolicy(fv) {
    frameFloor(fv);
    return fitCache.value?.policy || { compact: cardMetrics.compact };
  }

  /** Target values (positions, camera) for a mode and current floor. */
  function targetState(mode, current) {
    const ys = floorOffsets(floors.length, current, mode);
    let cam;
    if (!floors.length) {
      cam = { target: new Vector3(), viewH: 24, elev: ISO_ELEV };
    } else if (mode === 'building') {
      const fit = rig.fit(buildingFitBox(current), { elev: BUILDING_ELEV, margin: 1.08, reserveRight: floorLabelRoom() });
      cam = { ...fit, elev: BUILDING_ELEV };
    } else {
      cam = { ...frameFloor(floors[current]), elev: ISO_ELEV };
    }
    return {
      ys, cam, buildingT: mode === 'building' ? 1 : 0, shaftFade: mode === 'building' ? 0.0001 : SHAFT_FADE,
      groundY: mode === 'building' ? ys[0] - DIMS.slabH - 0.01 : 0,
    };
  }

  function applyVis() {
    for (const f of floors) f.setBuildingT(vis.buildingT);
    mats.slab.opacity = vis.buildingT;
    mats.pillar.opacity = vis.buildingT;
    mats.tx.fade.repeat.set(1, vis.shaftFade);
  }

  function setVisibleFloors(set) {
    floors.forEach((f, i) => { f.group.visible = set.has(i); });
  }

  function endVisibleSet(mode, current) {
    return mode === 'building' ? new Set(floors.map((_, i) => i)) : new Set([current]);
  }

  /** Jumps to a state without animation. */
  function snapTo(mode, current, { keepCamera = false } = {}) {
    tweens.clear();
    ride.set(0);
    ground.visible = true;
    cabin.group.visible = floors.length > 0;
    view.mode = mode;
    view.current = current;
    view.transitioning = false;
    view.revealed = false;
    const st = targetState(mode, current);
    floors.forEach((f, i) => { f.group.position.y = st.ys[i]; });
    vis.buildingT = st.buildingT;
    vis.shaftFade = st.shaftFade;
    applyVis();
    ground.position.y = st.groundY;
    if (!keepCamera) {
      userAdjusted = false;
      kinetic.set(0, 0, 0);
      rig.target.copy(st.cam.target);
      rig.viewH = st.cam.viewH;
      rig.elev = st.cam.elev;
      rig.apply();
    }
    setVisibleFloors(endVisibleSet(mode, current));
    fitShadow(floors[current]);
    updateCasters();
    syncPets();
    setCabinDisplay(numberOf(current));
    anchorsDirty = true;
    invalidate();
  }

  /** Animated transition to (mode, current). */
  function transitionTo(mode, current, duration) {
    if (reducedMotion || duration <= 0) {
      snapTo(mode, current);
      return;
    }
    // Camera moves (and a pending "focus this pod once there") belong to an earlier request.
    cancelCameraIntent();
    kinetic.set(0, 0, 0);
    const from = { mode: view.mode, current: view.current };
    const startYs = floors.map((f) => f.group.position.y);
    const st = targetState(mode, current);
    const startCam = { target: rig.target.clone(), viewH: rig.viewH, elev: rig.elev };
    const startVis = { ...vis };
    const startGround = ground.position.y;
    const involved = new Set([...endVisibleSet(mode, current)]);
    floors.forEach((f, i) => { if (f.group.visible) involved.add(i); });
    if (from.mode === 'floor' && mode === 'floor') {
      const lo = Math.min(from.current, current);
      const hi = Math.max(from.current, current);
      for (let i = lo; i <= hi; i++) involved.add(i);
    }
    setVisibleFloors(involved);
    view.mode = mode;
    view.current = current;
    view.transitioning = true;
    view.revealed = false;
    userAdjusted = false;
    fitShadow(floors[current]);
    floors.forEach((f, i) => f.setCastShadow(i === current || i === from.current));
    syncPets();
    const dir = current > from.current ? '▲' : current < from.current ? '▼' : '';
    setCabinDisplay(dir ? `${dir}${numberOf(current)}` : numberOf(current));
    anchorsDirty = true;
    const logA = Math.log(startCam.viewH);
    const logB = Math.log(st.cam.viewH);
    const riding = from.mode === 'floor' && mode === 'floor' && from.current !== current;
    if (riding) ground.visible = false;
    particles.clear();
    tweens.add({
      key: 'view',
      duration,
      ease: ease.inOutCubic,
      onUpdate: (k, raw) => {
        // Cards and name tags fade back in during the last stretch (the view has almost settled).
        if (raw >= REVEAL_AT && !view.revealed) view.revealed = true;
        floors.forEach((f, i) => { f.group.position.y = lerp(startYs[i], st.ys[i], k); });
        vis.buildingT = lerp(startVis.buildingT, st.buildingT, ease.inOutSine(k));
        vis.shaftFade = lerp(startVis.shaftFade, st.shaftFade, k);
        applyVis();
        ground.position.y = lerp(startGround, st.groundY, k);
        rig.target.lerpVectors(startCam.target, st.cam.target, k);
        rig.viewH = Math.exp(lerp(logA, logB, k));
        rig.elev = lerp(startCam.elev, st.cam.elev, k);
        rig.apply();
        if (riding) ride.set(Math.min(1, Math.sin(Math.PI * k) * 3));
      },
      onDone: () => {
        view.transitioning = false;
        if (!userAdjusted) {
          // The canvas may have been resized during the ride: land on the current framing.
          const end = targetState(mode, current).cam;
          rig.target.copy(end.target);
          rig.viewH = end.viewH;
          rig.elev = end.elev;
          rig.apply();
        }
        ride.set(0);
        ground.visible = true;
        setVisibleFloors(endVisibleSet(mode, current));
        updateCasters();
        setCabinDisplay(numberOf(current));
        anchorsDirty = true;
      },
    });
    invalidate();
  }

  /** Re-applies the default framing unless the user moved the camera (insets or card size changed). */
  function reframe() {
    if (!userAdjusted && floors.length && !view.transitioning) {
      const st = targetState(view.mode, view.current);
      tweens.cancel('camera');
      rig.target.copy(st.cam.target);
      rig.viewH = st.cam.viewH;
      rig.elev = st.cam.elev;
      rig.apply();
    }
    anchorsDirty = true;
    invalidate();
  }

  function cameraTo(target, viewH, duration = CAMERA_MS) {
    if (reducedMotion) {
      rig.target.copy(target);
      rig.viewH = viewH;
      rig.apply();
      anchorsDirty = true;
      invalidate();
      return;
    }
    const t0 = rig.target.clone();
    const a = Math.log(rig.viewH);
    const b = Math.log(viewH);
    tweens.add({
      key: 'camera',
      duration,
      ease: ease.inOutCubic,
      onUpdate: (k) => {
        rig.target.lerpVectors(t0, target, k);
        rig.viewH = Math.exp(lerp(a, b, k));
        rig.apply();
      },
    });
    invalidate();
  }

  /** The user or the API takes over the camera: drop pending camera moves. */
  function cancelCameraIntent() {
    tweens.cancel('camera');
    tweens.cancel('focus-after');
  }

  function zoomLimits() {
    const st = floors.length ? targetState(view.mode, view.current).cam.viewH : 24;
    return view.mode === 'building' ? [st * 0.3, st * 1.5] : [Math.max(5, st * 0.22), st * 1.7];
  }

  function panBounds() {
    if (!floors.length) return null;
    if (view.mode === 'building') return buildingFitBox(view.current).expandByScalar(4);
    const f = floors[view.current];
    return floorFitBox(f).expandByScalar(3);
  }

  function clampTarget() {
    const b = panBounds();
    if (!b) return;
    rig.target.x = clamp(rig.target.x, b.min.x, b.max.x);
    rig.target.z = clamp(rig.target.z, b.min.z, b.max.z);
    if (view.mode === 'building') rig.target.y = clamp(rig.target.y, b.min.y, b.max.y);
  }

  // ---------------------------------------------------------------- the pets

  /**
   * Gives the floors their pets: each created when its floor is first shown in floor view, kept with
   * the floor, removed when switched off; hidden in the building view.
   * @returns {boolean} true when something changed
   */
  function syncPets() {
    let changed = false;
    floors.forEach((f, i) => {
      for (const { kind } of PETS) {
        if (!petIds.get(kind).has(f.id)) {
          if (f.setPet(kind, false)) changed = true;
          continue;
        }
        // Its first rest counts from now (the clock is stale while the loop sleeps).
        const carry = petCarry.get(kind);
        if (!f.pets[kind] && view.mode === 'floor' && i === view.current
          && f.setPet(kind, true, Math.max(clockS, performance.now() / 1000), carry.get(f.id))) {
          carry.delete(f.id);
          changed = true;
        }
        const pet = f.pets[kind];
        if (pet) {
          // Only the floor the view goes to: the others stay hidden while floors slide past.
          const shown = view.mode === 'floor' && i === view.current;
          if (pet.group.visible !== shown) {
            pet.group.visible = shown;
            changed = true;
          }
        }
      }
    });
    return changed;
  }

  /** The floor whose pets may move now: the current one, in floor view. */
  function petFloor() {
    return view.mode === 'floor' ? floors[view.current] || null : null;
  }

  function updatePetEnv(f) {
    petEnv.hidden = isHidden();
    petEnv.building = view.mode === 'building';
    petEnv.riding = view.transitioning;
    petEnv.reducedMotion = reducedMotion;
    petEnv.avoid = f?.pets.chicken?.state || null;
  }

  /** Moves the current floor's pets; true when one of them asks for the next frame. */
  function updatePets(dt) {
    const f = petFloor();
    if (!f) return false;
    updatePetEnv(f);
    let active = false;
    for (const p of PETS) {
      const pet = f.pets[p.kind];
      if (!pet) continue;
      pet.update(dt, clockS, petEnv);
      if (p.fps(pet.state, petEnv) > 0) active = true;
    }
    return active;
  }

  /** Delay (ms from the frame at `now`) until the first resting pet gets up; null when none. */
  function petWakeMs(now) {
    const f = petFloor();
    if (!f) return null;
    updatePetEnv(f);
    let at = Infinity;
    for (const { kind } of PETS) {
      const pet = f.pets[kind];
      if (pet) at = Math.min(at, pet.wakeAt(petEnv));
    }
    return Number.isFinite(at) ? Math.max(0, at * 1000 - now) : null;
  }

  // ---------------------------------------------------------------- activity & status

  function floorOfAgent(id) {
    const i = agentFloor.get(id);
    return i === undefined ? null : floors[i];
  }

  function recomputeActivity() {
    for (const f of floors) {
      let working = 0;
      let waiting = 0;
      for (const r of f.crowd.rigs) {
        if (r.status === 'working') working++;
        else if (r.status === 'waiting') waiting++;
      }
      let doing = 0;
      for (const p of f.pods) doing += Number(deptStats.get(p.deptId)?.doing) || 0;
      const n = Math.max(1, f.crowd.rigs.length);
      f.setActivity(clamp(Math.max(working + waiting * 0.5, doing) / n * 1.4, 0, 1));
    }
  }

  function emitFor(fv) {
    return (r, kind) => {
      if (kind !== 'glyph') return;
      const slot = fv.deskSlots[r.info.deskIndex];
      const y = fv.group.position.y;
      const color = tokens.glyph === 'light' ? lighten(r.info.deptColor, 0.35) : mixHex(r.info.deptColor, '#1D1B18', 0.25);
      const base = slot ? new Vector3(slot.x, DIMS.podH + DIMS.deskH + 0.62, slot.z) : new Vector3(r.info.seatX, 1.6, r.info.seatZ);
      const h = hashString(`${r.id}${Math.floor(time * 10)}`);
      particles.emit({
        x: base.x + ((h % 100) / 100 - 0.5) * 0.5, y: y + base.y, z: base.z + (((h >>> 8) % 100) / 100 - 0.5) * 0.5,
        vx: ((h >>> 3) % 7 - 3) * 0.03, vy: 0.55, vz: ((h >>> 5) % 7 - 3) * 0.03,
        life: 1.8, glyph: h % 12, color, size: 0.44, drag: 0.3,
      });
    };
  }

  function doneBurst(fv, r) {
    if (reducedMotion) return;
    const p = fv.crowd.headTop(r, new Vector3());
    p.y += fv.group.position.y + 0.1;
    for (let i = 0; i < 14; i++) {
      const a = (i / 14) * Math.PI * 2;
      particles.emit({
        x: p.x, y: p.y, z: p.z, vx: Math.cos(a) * 1.3, vy: 1.4 + (i % 3) * 0.3, vz: Math.sin(a) * 1.3,
        life: 1.1, glyph: i % 2 ? 15 : 3, color: i % 3 ? '#3FA372' : '#E6C35C', size: 0.26, drag: 2.2, gravity: 2.4,
      });
    }
  }

  // ---------------------------------------------------------------- picking & hover

  const _ray = new Ray();
  const _box = new Box3();
  const _hit = new Vector3();

  function pick(px, py) {
    if (!floors.length || view.transitioning) return null;
    rig.rayAt(px, py, _ray);
    let best = null;
    const consider = (kind, id, box) => {
      const hit = _ray.intersectBox(box, _hit);
      if (!hit) return;
      const d = _ray.origin.distanceTo(hit);
      if (!best || d < best.d) best = { kind, id, d };
    };
    if (view.mode === 'building') {
      floors.forEach((f) => { if (f.group.visible) consider('floor', f.id, f.slabBox(_box)); });
      return best ? { kind: best.kind, id: best.id } : null;
    }
    const f = floors[view.current];
    const y = f.group.position.y;
    for (const r of f.crowd.rigs) {
      const p = r.root.position;
      const hTop = DIMS.podH + (r.stand > 0.5 ? 1.7 : 1.45);
      _box.min.set(p.x - 0.36, y + DIMS.podH, p.z - 0.36);
      _box.max.set(p.x + 0.36, y + hTop, p.z + 0.36);
      consider('agent', r.id, _box);
    }
    for (const pod of f.pods) consider('dept', pod.deptId, f.podBox(pod, _box));
    return best ? { kind: best.kind, id: best.id } : null;
  }

  function setHover(hit) {
    const same = (hit === null && hover === null) || (hit && hover && hit.kind === hover.kind && hit.id === hover.id);
    if (same) return;
    hover = hit;
    canvas.style.cursor = hit ? 'pointer' : '';
    applyHighlights();
    try { cb.hover?.(hit ? { kind: hit.kind, id: hit.id } : null); } catch (err) { console.error(err); }
    invalidate();
  }

  function applyHighlights() {
    const dept = hover?.kind === 'dept' ? hover.id : externalDept;
    floors.forEach((f) => {
      f.setHoverDept(f.podById.has(dept) ? dept : null);
      f.setFloorHover(hover?.kind === 'floor' && hover.id === f.id);
    });
    hoverRing.visible = false;
    if (hover?.kind === 'agent') {
      const f = floorOfAgent(hover.id);
      const r = f?.crowd.byId.get(hover.id);
      if (r) {
        hoverRingMat.color.set(tokens.name === 'dark' ? lighten(r.info.deptColor, 0.2) : r.info.deptColor);
        hoverRing.visible = true;
        positionHoverRing(f, r);
      }
    }
  }

  function positionHoverRing(f, r) {
    const p = r.root.position;
    hoverRing.position.set(p.x, f.group.position.y + DIMS.podH + 0.012, p.z);
    hoverRing.scale.setScalar(0.5);
  }

  // ---------------------------------------------------------------- controls

  const detachControls = attachControls(canvas, {
    onPan(x0, y0, x1, y1) {
      const h = view.mode === 'building' ? rig.target.y : DIMS.podH;
      const a = rig.groundAt(x0, y0, h);
      const b = rig.groundAt(x1, y1, h);
      if (!a || !b) return;
      const dx = a.x - b.x;
      const dz = a.z - b.z;
      cancelCameraIntent();
      rig.target.x += dx;
      rig.target.z += dz;
      clampTarget();
      rig.apply();
      const now = performance.now();
      panSamples.push({ t: now, dx, dz });
      panSamples = panSamples.filter((p) => now - p.t < 120);
      userAdjusted = true;
      anchorsDirty = true;
      invalidate();
    },
    onPanEnd() {
      // Glide from the velocity of the last ~120 ms of dragging, capped relative to the view.
      const now = performance.now();
      const recent = panSamples.filter((p) => now - p.t < 120);
      panSamples = [];
      kinetic.set(0, 0, 0);
      if (reducedMotion || recent.length < 2 || now - recent[recent.length - 1].t > 60) return;
      const span = Math.max(0.03, (recent[recent.length - 1].t - recent[0].t) / 1000);
      for (const p of recent) {
        kinetic.x += p.dx;
        kinetic.z += p.dz;
      }
      kinetic.divideScalar(span);
      const max = rig.viewH * 0.9;
      if (kinetic.length() > max) kinetic.setLength(max);
      invalidate();
    },
    onZoom(factor, x, y) {
      zoomAt(factor, x, y);
    },
    onClick(x, y) {
      const hit = pick(x, y);
      if (!hit) return;
      try {
        if (hit.kind === 'agent') cb.agent?.(hit.id);
        else if (hit.kind === 'dept') cb.dept?.(hit.id);
        else if (hit.kind === 'floor') {
          api.showFloor(hit.id);
          cb.floor?.(hit.id);
        }
      } catch (err) {
        console.error(err);
      }
    },
    onDoubleClick(x, y) {
      const hit = pick(x, y);
      if (!hit) return;
      if (hit.kind === 'dept') api.focusDept(hit.id);
      else if (hit.kind === 'agent') {
        const f = floorOfAgent(hit.id);
        const r = f?.crowd.byId.get(hit.id);
        if (r) api.focusDept(r.info.deptId);
      }
    },
    onHover(x, y) {
      hoverPending = { x, y };
      invalidate();
    },
    onLeave() {
      hoverPending = null;
      setHover(null);
    },
    onInteract(active) {
      interacting = active;
      if (active) kinetic.set(0, 0, 0);
      invalidate();
    },
  });

  function zoomAt(factor, x, y) {
    if (!floors.length) return;
    const [lo, hi] = zoomLimits();
    const next = clamp(rig.viewH * factor, lo, hi);
    if (next === rig.viewH) return;
    cancelCameraIntent();
    const h = view.mode === 'building' ? rig.target.y : DIMS.podH;
    const before = rig.groundAt(x, y, h);
    rig.viewH = next;
    rig.apply();
    const after = rig.groundAt(x, y, h);
    if (before && after) {
      rig.target.x += before.x - after.x;
      rig.target.z += before.z - after.z;
    }
    clampTarget();
    rig.apply();
    userAdjusted = true;
    anchorsDirty = true;
    invalidate();
  }

  // ---------------------------------------------------------------- loop

  // Nothing is drawn while the page is hidden (onVisibility resumes); embedded previews can opt out
  // with options.ignoreHidden.
  function invalidate() {
    if (disposed) return;
    dirty = true;
    if (timer) {
      clearTimeout(timer);
      timer = 0;
    }
    if (!raf && !isHidden()) raf = requestAnimationFrame(frame);
  }

  /** Next frame in `waitMs` ms: a timeout first when it is far, so slow paces stay cheap. */
  function schedule(waitMs) {
    if (disposed || raf || timer || isHidden()) return;
    if (waitMs > 20) {
      timer = setTimeout(() => {
        timer = 0;
        if (!raf && !disposed && !isHidden()) raf = requestAnimationFrame(frame);
      }, waitMs - 10);
    } else raf = requestAnimationFrame(frame);
  }

  /** What moves now → wanted frame rate (0 = nothing, stop). */
  function currentFps() {
    const idleAnim = !reducedMotion && floors.some((f) => f.group.visible && f.crowd.rigs.length);
    return targetFps({
      motion: tweens.active || interacting || kinetic.lengthSq() > 1e-4 || !!hoverPending,
      active: particles.active || pulses.active || agentsActive || petsActive || time < settleUntil,
      ambient: idleAnim,
    });
  }

  function frame(now) {
    raf = 0;
    if (disposed) return;
    if (isHidden()) {
      last = 0;
      return;
    }
    clockS = now / 1000;
    const pace = throttleFrame(now, lastRender, currentFps());
    if (!dirty && !pace.render) {
      schedule(pace.waitMs);
      return;
    }
    dirty = false;
    // Simulation steps are capped (a long frame must not teleport the agents) but cover a slow
    // paced frame (8 fps), so idle life keeps its real-time speed; transitions follow the wall
    // clock, so a floor switch lasts its duration even when frames are slow or throttled.
    const realMs = last ? Math.max(0, now - last) : 1000 / 60;
    const dt = Math.min(0.2, realMs / 1000);
    last = now;
    update(dt, realMs);
    render();
    lastRender = pace.render ? pace.next : now;
    fps.frames++;
    if (now - fps.since > 1000) {
      fps.value = Math.round((fps.frames * 1000) / (now - fps.since));
      fps.frames = 0;
      fps.since = now;
    }
    const want = currentFps();
    if (!want) {
      last = 0;
      // Nothing moves: only a resting pet wakes the loop up, when its rest ends.
      const wake = petWakeMs(now);
      if (wake !== null) schedule(wake);
      return;
    }
    schedule(throttleFrame(now + 0.001, lastRender, want).waitMs);
  }

  function update(dt, tweenMs = dt * 1000) {
    time += dt;
    tweens.update(tweenMs);
    if (!interacting && kinetic.lengthSq() > 1e-4) {
      rig.target.addScaledVector(kinetic, dt);
      kinetic.multiplyScalar(Math.exp(-6 * dt));
      clampTarget();
      rig.apply();
      anchorsDirty = true;
    }
    if (hoverPending) {
      const p = hoverPending;
      hoverPending = null;
      setHover(pick(p.x, p.y));
    }
    const bubblePx = coarse ? 22 : 26;
    const ctxBase = {
      reducedMotion,
      bubbleScale: clamp(bubblePx * rig.unitsPerPixel(), 0.42, 2.4),
    };
    let active = false;
    for (const f of floors) {
      if (!f.group.visible) continue;
      if (f.update(dt, time, { ...ctxBase, emit: view.transitioning ? null : emitFor(f) })) active = true;
    }
    agentsActive = active;
    petsActive = updatePets(dt);
    if (hover?.kind === 'agent') {
      const f = floorOfAgent(hover.id);
      const r = f?.crowd.byId.get(hover.id);
      if (r) positionHoverRing(f, r);
    }
    particles.update(dt);
    pulses.update(dt);
    mats.screen.uniforms.uTime.value = reducedMotion ? 0 : time;
    mats.glyphs.uniforms.uScale.value = (rig.height * renderer.getPixelRatio()) / rig.viewH;
    anchorsDirty = true;
  }

  function render() {
    if (rig.width < 2 || rig.height < 2) return;
    renderer.render(scene, rig.camera);
    computeAnchors();
    if (cb.anchors) {
      try { cb.anchors(anchors.map((a) => ({ ...a }))); } catch (err) { console.error(err); }
    }
  }

  const _a = new Vector3();

  /**
   * Card layout of the current floor on screen (framing.layoutCards), inside the free area; kept
   * until the camera, the canvas, the insets or the card size change.
   */
  function currentPlacement(f) {
    const y = f.group.position.y;
    const c = rig.camera.position;
    const area = freeArea();
    const key = [f.id, signature, y.toFixed(3), c.x.toFixed(3), c.y.toFixed(3), c.z.toFixed(3), rig.viewH.toFixed(4),
      rig.elev.toFixed(4), rig.width, rig.height, area.left, area.top, area.right, area.bottom,
      cardMetrics.w, cardMetrics.h, cardMetrics.compact].join('|');
    if (key === placementKey) return placement;
    const geo = floorGeo(f);
    const proj = (p) => {
      const q = rig.project(_a.set(p.x, p.y + y, p.z));
      return { x: q.x, y: q.y };
    };
    const shapes = podShapes(geo, proj);
    const inner = 6;
    placement = layoutCards(shapes, {
      ...cardPolicy(f), card: cardMetrics, gap: cardMetrics.gap, center: proj(ORIGIN),
      obstacles: [convexHull(geo.cabin.map(proj))], prev: placement,
      bounds: { left: area.left + inner, top: area.top + inner, right: area.right - inner, bottom: area.bottom - inner },
    });
    placement.shapes = new Map(shapes.map((sh) => [sh.id, sh]));
    placementKey = key;
    return placement;
  }

  function computeAnchors() {
    const out = [];
    const floorView = view.mode === 'floor' && (!view.transitioning || view.revealed);
    const buildingView = view.mode === 'building' && !view.transitioning;
    floors.forEach((f, i) => {
      const y = f.group.position.y;
      const onScreen = f.group.visible;
      const isCurrent = i === view.current;
      const cards = floorView && isCurrent && onScreen && f.pods.length ? currentPlacement(f) : null;
      for (const pod of f.pods) {
        const shape = cards?.shapes?.get(pod.deptId);
        const top = rig.project(_a.set(pod.x, y + DIMS.podH, pod.z));
        const ax = shape ? shape.top.x : top.x;
        const ay = shape ? shape.top.y : top.y - 60;
        const card = cards?.get(pod.deptId);
        out.push({
          kind: 'dept', id: pod.deptId, floorId: f.id, x: ax, y: ay, podX: top.x, podY: top.y,
          visible: floorView && isCurrent && onScreen && top.inView,
          card: card ? {
            mode: card.mode, side: card.side, forced: !!card.forced,
            rect: { ...card.rect }, lead: { ...card.lead },
          } : null,
        });
      }
      for (const r of f.crowd.rigs) {
        f.crowd.headTop(r, _a);
        _a.y += y + 0.22;
        const p = rig.project(_a);
        out.push({
          kind: 'agent', id: r.id, deptId: r.info.deptId, floorId: f.id, x: p.x, y: p.y, status: r.status,
          visible: floorView && isCurrent && onScreen && p.inView,
        });
      }
      const s = f.layout.slab;
      const fp = rig.project(_a.set(s.x + s.w / 2, y, s.z - s.d / 2));
      out.push({ kind: 'floor', id: f.id, x: fp.x, y: fp.y, visible: buildingView && onScreen && fp.inView });
    });
    anchors = out;
    anchorsDirty = false;
  }

  // ---------------------------------------------------------------- resize, visibility, motion

  function resize() {
    const w = container.clientWidth;
    const h = container.clientHeight;
    if (!w || !h) return;
    renderer.setPixelRatio(scenePixelRatio(window.devicePixelRatio));
    renderer.setSize(w, h, false);
    rig.setSize(w, h);
    if (!userAdjusted && !view.transitioning && floors.length) {
      const st = targetState(view.mode, view.current);
      rig.target.copy(st.cam.target);
      rig.viewH = st.cam.viewH;
      rig.elev = st.cam.elev;
      rig.apply();
    } else {
      const [lo, hi] = zoomLimits();
      rig.viewH = clamp(rig.viewH, lo, hi);
      rig.apply();
    }
    anchorsDirty = true;
    invalidate();
  }

  const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => resize()) : null;
  ro?.observe(container);
  if (!ro) window.addEventListener('resize', resize);

  function onVisibility() {
    if (isHidden()) {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
      if (timer) clearTimeout(timer);
      timer = 0;
      last = 0;
    } else {
      last = 0;
      invalidate();
    }
  }
  document.addEventListener('visibilitychange', onVisibility);

  // A change of the system preference always wins (the UI may pass the initial value as a boolean
  // and never tell us again).
  function onMotionPref(e) {
    api.setReducedMotion(!!e.matches);
  }
  mqReduced?.addEventListener?.('change', onMotionPref);

  // Canvas text needs the web fonts: redraw once they are ready.
  if (document.fonts?.load) {
    Promise.all([
      document.fonts.load(`600 44px ${FONT_MONO}`), document.fonts.load(`500 34px ${FONT_MONO}`),
    ]).catch(() => {}).then(() => document.fonts.ready).then(() => {
      if (disposed) return;
      for (const f of floors) f.applyTheme(tokens);
      setCabinDisplay(numberOf(view.current));
      invalidate();
    }).catch(() => {});
  }

  // ---------------------------------------------------------------- public API

  const api = {
    /** (Re)builds the floors. Keeps the camera when the current floor still exists. */
    setOffice(office) {
      for (const p of PETS) petIds.set(p.kind, p.floorIds(office || {}));
      const sig = officeSignature(office || {});
      if (sig === signature) {
        // Same structure: only a floor setting may have changed (a pet), no rebuild.
        if (syncPets()) invalidate();
        return;
      }
      const first = signature === null;
      signature = sig;
      const prevId = floors[view.current]?.id;
      const prevMode = view.mode;
      // A rebuilt floor keeps its pets where they were (when that spot is still free).
      for (const { kind } of PETS) {
        const carry = petCarry.get(kind);
        for (const f of floors) {
          const s = f.pets[kind]?.state;
          if (s) carry.set(f.id, { x: s.x, z: s.z, yaw: s.yaw });
        }
        const ids = petIds.get(kind);
        for (const id of carry.keys()) if (!ids.has(id)) carry.delete(id);
      }
      for (const f of floors) {
        root.remove(f.group);
        f.dispose();
      }
      floors = [];
      agentFloor.clear();
      deptFloor.clear();
      setHover(null);
      particles.clear();
      pulses.clear();
      const org = organizeOffice(office || {});
      const slabRect = buildingSlab(org.map(({ depts }) => floorLayout(depts.map((d) => ({ id: d.dept.id, count: d.agents.length })))));
      org.forEach(({ floor, depts }, i) => {
        const fv = new FloorView({ floor, depts, index: i, isTop: i === org.length - 1, geos, mats, slabRect });
        root.add(fv.group);
        floors.push(fv);
        for (const d of depts) {
          deptFloor.set(d.dept.id, i);
          for (const a of d.agents) agentFloor.set(a.id, i);
        }
      });
      for (const [id, st] of statuses) {
        const f = floorOfAgent(id);
        if (f) f.crowd.setStatus(id, st === 'done' ? 'idle' : st, time);
      }
      for (const f of floors) f.update(0, time, { reducedMotion: true, bubbleScale: 0.8 });
      recomputeActivity();
      applyHighlights();
      let idx = floors.findIndex((f) => f.id === prevId);
      // Keep a camera the user placed; otherwise re-frame (the floor may have grown or shrunk).
      const keep = idx >= 0 && !first && userAdjusted;
      if (idx < 0) idx = 0;
      snapTo(floors.length ? prevMode : 'floor', Math.min(idx, Math.max(0, floors.length - 1)), { keepCamera: keep });
      if (keep) {
        clampTarget();
        rig.apply();
      }
      // Compile the shaders of the hidden floors and of the ride shaft now: the first elevator ride
      // must not stall on its first frame.
      try { renderer.compileAsync?.(scene, rig.camera)?.catch?.(() => {}); } catch { /* optional */ }
    },

    /**
     * Updates an agent's live status: idle | working | waiting | error | done (a short flash, then
     * idle). Accepts `{status, taskId, activity}` (SPEC) or a bare status string.
     */
    setAgentStatus(agentId, st) {
      const s = normalizeStatus(st && typeof st === 'object' ? st.status : st);
      statuses.set(agentId, s);
      const f = floorOfAgent(agentId);
      if (!f) return;
      const r = f.crowd.byId.get(agentId);
      const prev = r?.status;
      f.crowd.setStatus(agentId, s, time);
      if (s === 'done' && r && f.group.visible) doneBurst(f, r);
      if (s === 'done') statuses.set(agentId, 'idle');
      if (prev !== s) recomputeActivity();
      anchorsDirty = true;
      invalidate();
    },

    /** Stores department counters (used for the building view activity glow). */
    setDeptStats(deptId, stats) {
      deptStats.set(deptId, stats && typeof stats === 'object' ? { ...stats } : {});
      recomputeActivity();
      invalidate();
    },

    /** Elevator transition to a floor (no-op when already there). */
    showFloor(floorId, opts) {
      const idx = floors.findIndex((f) => f.id === floorId);
      if (idx < 0) return;
      if (view.mode === 'floor' && view.current === idx) return;
      transitionTo('floor', idx, wantsAnimation(opts) ? FLOOR_SWITCH_MS : 0);
    },

    /** Exploded view of every floor stacked (no-op when already shown). */
    showBuilding(opts) {
      if (view.mode === 'building' || !floors.length) return;
      transitionTo('building', view.current, wantsAnimation(opts) ? VIEW_SWITCH_MS : 0);
    },

    /** Frames a department's pod (switching floor first when needed). */
    focusDept(deptId) {
      const idx = deptFloor.get(deptId);
      if (idx === undefined) return;
      const go = () => {
        const f = floors[idx];
        const pod = f?.podById.get(deptId);
        if (!pod) return;
        const box = new Box3(
          new Vector3(pod.x - pod.w / 2 - 1.2, 0, pod.z - pod.d / 2 - 1.2),
          new Vector3(pod.x + pod.w / 2 + 1.2, 2.6, pod.z + pod.d / 2 + 1.2),
        );
        const fit = rig.fit(box, { elev: ISO_ELEV, margin: 1.1 });
        userAdjusted = true;
        kinetic.set(0, 0, 0);
        cameraTo(fit.target, fit.viewH);
      };
      if (view.mode !== 'floor' || view.current !== idx) {
        transitionTo('floor', idx, FLOOR_SWITCH_MS);
        if (reducedMotion) go();
        else tweens.add({ key: 'focus-after', duration: FLOOR_SWITCH_MS + 20, onUpdate: () => {}, onDone: go });
      } else {
        tweens.cancel('focus-after');
        go();
      }
    },

    /** Re-frames the current floor or the building. */
    resetView() {
      if (!floors.length) return;
      tweens.cancel('focus-after');
      if (view.transitioning) return; // the transition already ends on the default framing
      const st = targetState(view.mode, view.current);
      userAdjusted = false;
      kinetic.set(0, 0, 0);
      cameraTo(st.cam.target, st.cam.viewH, 500);
    },

    /** Zooms in (delta > 0) or out (delta < 0) around the centre of the view; delta 1 ≈ one step. */
    zoom(delta = 1) {
      const d = Number(delta);
      if (!floors.length || !Number.isFinite(d) || d === 0 || view.transitioning) return;
      const [lo, hi] = zoomLimits();
      const next = clamp(rig.viewH / 1.3 ** clamp(d, -6, 6), lo, hi);
      userAdjusted = true;
      kinetic.set(0, 0, 0);
      tweens.cancel('focus-after');
      cameraTo(rig.target.clone(), next, 260);
    },

    /** A connector tool was used by a department: a light pulse rises from its pod. */
    pulseConnector(serverKey, deptId) {
      const idx = deptFloor.get(deptId);
      if (idx === undefined) return;
      const f = floors[idx];
      const pod = f.podById.get(deptId);
      if (!pod || !f.group.visible) return;
      const color = tokens.name === 'dark' ? lighten(pod.color, 0.25) : pod.color;
      pulses.spawn({ x: pod.x, y: f.group.position.y + DIMS.podH, z: pod.z }, color, reducedMotion, tokens.name === 'dark');
      invalidate();
    },

    /** Screen anchors (CSS px relative to the container) for HTML cards, tags and floor labels. */
    getAnchors() {
      if (anchorsDirty) computeAnchors();
      return anchors.map((a) => ({ ...a }));
    },

    setTheme(name) {
      tokens = themeTokens(name);
      applyTheme(mats, tokens);
      applyLightsTheme();
      for (const f of floors) {
        f.applyTheme(tokens);
        f.refreshScreens();
      }
      applyHighlights();
      invalidate();
    },

    resize,

    dispose() {
      if (disposed) return;
      disposed = true;
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
      if (timer) clearTimeout(timer);
      timer = 0;
      detachControls();
      ro?.disconnect();
      if (!ro) window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', onVisibility);
      mqReduced?.removeEventListener?.('change', onMotionPref);
      tweens.clear();
      pulses.clear();
      for (const f of floors) f.dispose();
      floors = [];
      particles.dispose();
      cabin.dispose();
      ride.dispose();
      groundGeo.dispose();
      hoverRingMat.dispose();
      disposeMaterials(mats);
      disposeAll(geos);
      renderer.dispose();
      renderer.forceContextLoss?.();
      canvas.remove();
    },

    // ---- Extensions (not in SPEC §10, optional for the UI) ----

    /** Highlights a department's pod (e.g. while its HTML card is hovered); null clears. */
    highlightDept(deptId) {
      externalDept = deptId || null;
      applyHighlights();
      invalidate();
    },

    /** Screen areas covered by UI (px): fits centre the office in what remains visible. */
    setViewInsets(insets) {
      rig.setInsets(insets && typeof insets === 'object' ? insets : {});
      reframe();
    },

    /**
     * Size of the HTML department cards (px), so the floor framing leaves room for them and the
     * anchors carry a layout that fits: `{w, h, gap, compact}` (compact: pills only, phones);
     * `labelW`: the widest floor label of the building view, which keeps room for the labels.
     */
    setCardMetrics(m) {
      if (!m || typeof m !== 'object') return;
      const num = (v, d, lo, hi) => (Number.isFinite(Number(v)) && v !== null && v !== '' ? clamp(Number(v), lo, hi) : d);
      const next = {
        w: num(m.w, cardMetrics.w, 60, 400),
        h: num(m.h, cardMetrics.h, 16, 400),
        gap: num(m.gap, cardMetrics.gap, 0, 60),
        compact: typeof m.compact === 'boolean' ? m.compact : cardMetrics.compact,
        labelW: num(m.labelW, cardMetrics.labelW, 0, 600),
      };
      if (next.w === cardMetrics.w && next.h === cardMetrics.h && next.gap === cardMetrics.gap && next.compact === cardMetrics.compact
        && next.labelW === cardMetrics.labelW) return;
      cardMetrics = next;
      reframe();
    },

    /** Overrides prefers-reduced-motion (until the system preference changes again). */
    setReducedMotion(on) {
      if (typeof on !== 'boolean' || on === reducedMotion) return;
      reducedMotion = on;
      // Give the agents a moment to settle into their still (or moving) poses.
      settleUntil = time + 1;
      if (on) {
        particles.clear();
        kinetic.set(0, 0, 0);
      }
      invalidate();
    },

    /** Internals for the dev harness only (requires the `debug: true` option). */
    debug() {
      if (options.debug !== true) return null;
      const step = (ms = 16) => {
        let left = ms;
        while (left > 0) {
          const d = Math.min(left, 50);
          clockS += d / 1000;
          update(d / 1000);
          left -= d;
        }
        render();
      };
      return { scene, renderer, mats, floors, rig, root, step };
    },

    /** Debug counters for the dev harness. */
    getStats() {
      return {
        fps: fps.value, drawCalls: renderer.info.render.calls, triangles: renderer.info.render.triangles,
        floors: floors.length, agents: agentFloor.size, mode: view.mode, current: floors[view.current]?.id ?? null,
      };
    },
  };

  resize();
  invalidate();
  return api;
}
